// The confirmation text, for Admin's preview.
//
// The text is written and sent by the server after a patient books held
// times through a link (backend/src/services/bookingConfirmationText.ts).
// Admin shows the practice what a patient would read before saving its own
// wording, and that preview needs the same words here. The two are pinned to
// the same literals by their tests; change one and the other's test says so.
import { offerWhen } from './offerMessage';

export const CONFIRMATION_TAGS = ['firstName', 'service', 'provider', 'when', 'phone'] as const;

/** The standard wording with its tags — what Admin offers as the place to start from. */
export const STANDARD_CONFIRMATION_TEMPLATE =
  'Hi {firstName} — you are booked for a {service} with {provider} on {when}. '
  + 'If anything changes, just reply to this text.';

interface Booked { serviceName?: string | null; providerName?: string | null; date?: string | null; time?: string | null; }

const clean = (value?: string | null): string => String(value || '').trim();
const firstName = (full?: string | null): string => clean(full).split(/\s+/)[0] || 'there';
const what = (a: Booked): string =>
  `${clean(a.serviceName) || 'appointment'}${clean(a.providerName) ? ` with ${clean(a.providerName)}` : ''}`;

const article = (serviceName?: string | null): string => (/^[aeiou]/i.test(clean(serviceName)) ? 'an' : 'a');

export function confirmationText({ patientName, appointments, phone, template }: {
  patientName: string;
  appointments: Booked[];
  phone?: string | null;
  template?: string | null;
}): string {
  const list = Array.isArray(appointments) ? appointments : [];
  const number = clean(phone);
  const change = `If anything changes, just reply to this text${number ? ` or call us at ${number}` : ''}.`;
  const hi = `Hi ${firstName(patientName)}`;

  if (list.length > 1) {
    const lines = list.map((a, i) => `${i + 1}. ${what(a)} — ${offerWhen(a)}`);
    return [`${hi} — you are booked for ${list.length} appointments:`, ...lines, change].join('\n\n');
  }

  const only = list[0] || {};
  const when = only.date && only.time ? offerWhen(only) : '';

  if (clean(template)) {
    const values: Record<string, string> = {
      firstName: firstName(patientName),
      service: clean(only.serviceName) || 'appointment',
      provider: clean(only.providerName),
      when,
      phone: number,
    };
    return String(template).trim().replace(/\{(\w+)\}/g, (whole, name: string) =>
      ((CONFIRMATION_TAGS as readonly string[]).includes(name) ? values[name] : whole));
  }

  return `${hi} — you are booked for ${article(only.serviceName)} ${what(only)}${when ? ` on ${when}` : ''}. ${change}`;
}
